import React, {useEffect, useState, useRef} from 'react';

// Declare browser globals for TypeScript
declare const alert: (message: string) => void;
import {View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Image} from 'react-native';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {Html5Qrcode} from 'html5-qrcode';
import {spacing, borderRadius, touchTargets} from '../constants';
import {useAppDispatch} from '../store';
import {loginWithQr} from '../store/authSlice';
import {getDeviceInfo} from '../utils/deviceInfo';
import type {AppDispatch} from '../store';

// Curated picsum photo IDs - same as HomeScreen
const CURATED_PHOTO_IDS = [
  15, 28, 29, 36, 39, 42, 54, 65, 82, 106,
  110, 129, 142, 152, 164, 167, 193, 197, 200, 244,
  306, 318, 325, 349, 392, 399, 429, 433, 447, 452,
];

function getBackgroundPhoto(): string {
  const idKey = 'homeBackgroundPhotoId';
  try {
    const storedId = localStorage.getItem(idKey);
    if (storedId) {
      return `https://picsum.photos/id/${storedId}/1920/1080`;
    }
  } catch {}
  // Use a random one if no stored ID
  const id = CURATED_PHOTO_IDS[Math.floor(Math.random() * CURATED_PHOTO_IDS.length)];
  return `https://picsum.photos/id/${id}/1920/1080`;
}

export default function QRScanScreen() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch() as AppDispatch;
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [isScanning, setIsScanning] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [backgroundPhoto] = useState(getBackgroundPhoto);

  useEffect(() => {
    startScanner();

    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    try {
      setIsScanning(true);
      setCameraError(null);

      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;

      await scanner.start(
        {facingMode: 'environment'},
        {
          fps: 10,
          qrbox: {width: 250, height: 250},
        },
        onScanSuccess,
        () => {} // Ignore scan failures (no QR found yet)
      );
    } catch (err: any) {
      console.error('Camera error:', err);
      setCameraError(
        err.message?.includes('Permission')
          ? t('qrScan.cameraPermissionDenied')
          : t('qrScan.cameraError')
      );
      setIsScanning(false);
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
      } catch {
        // Ignore stop errors
      }
    }
  };

  const onScanSuccess = async (decodedText: string) => {
    // Validate that it looks like a valid token (base64url encoded, ~43 chars)
    if (!decodedText || decodedText.length < 20 || decodedText.length > 100) {
      return;
    }

    // Check if it looks like a base64url token
    if (!/^[A-Za-z0-9_-]+$/.test(decodedText)) {
      return;
    }

    await stopScanner();
    setIsScanning(false);
    setIsLoggingIn(true);
    setError(null);

    try {
      await dispatch(loginWithQr({qrToken: decodedText, deviceInfo: getDeviceInfo()})).unwrap();
    } catch (err: any) {
      const errorMessage = typeof err === 'string'
        ? err
        : err?.message || t('qrScan.failed');

      let userMessage: string;
      if (errorMessage.toLowerCase().includes('expired')) {
        userMessage = t('qrScan.expired');
      } else if (errorMessage.toLowerCase().includes('invalid')) {
        userMessage = t('qrScan.invalid');
      } else {
        userMessage = errorMessage;
      }

      setIsLoggingIn(false);
      alert(userMessage);
      startScanner();
    }
  };

  const handleBack = () => {
    stopScanner();
    navigate('/');
  };

  return (
    <View style={styles.container}>
      {/* Background Image */}
      <Image
        source={{uri: backgroundPhoto}}
        style={styles.backgroundImage}
        resizeMode="cover"
      />

      {/* Gradient Overlay */}
      <View style={styles.gradientOverlay} />

      {/* Header */}
      <View style={styles.header}>
        <View style={styles.logoContainer}>
          <Image
            source={{uri: '/icon.png'}}
            style={styles.logoImage}
            resizeMode="contain"
          />
          <Text style={styles.logoText}>{t('home.appName')}</Text>
        </View>
      </View>

      {/* Center Card */}
      <View style={styles.cardContainer}>
        <View style={styles.card}>
          <Text style={styles.title}>{t('qrScan.title')}</Text>

          {!cameraError && !isLoggingIn && (
            <Text style={styles.instructions}>{t('qrScan.instruction')}</Text>
          )}

          {/* QR Scanner */}
          {!cameraError && !isLoggingIn && (
            <View style={styles.scannerWrapper}>
              <div
                id="qr-reader"
                ref={containerRef as any}
                style={{
                  width: '100%',
                  maxWidth: 280,
                  aspectRatio: '1',
                  borderRadius: 16,
                  overflow: 'hidden',
                }}
              />
            </View>
          )}

          {/* Hidden div for scanner when not visible */}
          {(cameraError || isLoggingIn) && (
            <div id="qr-reader" style={{display: 'none'}} />
          )}

          {cameraError && (
            <View style={styles.errorContainer}>
              <View style={styles.errorIconContainer}>
                <Text style={styles.materialIcon}>videocam_off</Text>
              </View>
              <Text style={styles.errorText}>{cameraError}</Text>
              <TouchableOpacity
                style={styles.primaryButton}
                onPress={() => {
                  setCameraError(null);
                  startScanner();
                }}>
                <Text style={styles.primaryButtonText}>{t('qrScan.tryAgain')}</Text>
              </TouchableOpacity>
            </View>
          )}

          {isLoggingIn && (
            <View style={styles.loggingInContainer}>
              <ActivityIndicator size="large" color="#ee8c2b" />
              <Text style={styles.loggingInText}>{t('qrScan.loggingIn')}</Text>
            </View>
          )}

          {/* Back Button */}
          <TouchableOpacity
            style={styles.backButton}
            onPress={handleBack}
            activeOpacity={0.8}>
            <Text style={styles.backButtonText}>← {t('common.back')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  backgroundImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoImage: {
    width: 40,
    height: 40,
    marginRight: spacing.sm,
    borderRadius: 8,
  },
  logoText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 20,
    fontWeight: '700',
    color: '#fff',
  },
  cardContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.xl,
  },
  card: {
    backgroundColor: 'rgba(255, 250, 245, 0.95)',
    borderRadius: 40,
    padding: spacing.xl,
    width: '100%',
    maxWidth: 420,
    alignItems: 'center',
    // @ts-ignore - web-specific style
    backdropFilter: 'blur(20px)',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 10},
    shadowOpacity: 0.3,
    shadowRadius: 30,
    elevation: 20,
  },
  title: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 24,
    fontWeight: '700',
    color: '#221910',
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  instructions: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 15,
    fontWeight: '400',
    color: '#666',
    textAlign: 'center',
    marginBottom: spacing.lg,
    lineHeight: 22,
  },
  scannerWrapper: {
    width: '100%',
    maxWidth: 280,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
    borderRadius: 16,
    overflow: 'hidden',
  },
  errorContainer: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
  },
  errorIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(220, 38, 38, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.md,
  },
  materialIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 28,
    color: '#dc2626',
  },
  errorText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginBottom: spacing.lg,
    lineHeight: 20,
  },
  primaryButton: {
    backgroundColor: '#ee8c2b',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.xl,
    borderRadius: borderRadius.md,
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#ee8c2b',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
  },
  primaryButtonText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  loggingInContainer: {
    alignItems: 'center',
    paddingVertical: spacing.xl,
  },
  loggingInText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 15,
    color: '#666',
    marginTop: spacing.md,
  },
  backButton: {
    paddingVertical: spacing.md,
    marginTop: spacing.sm,
  },
  backButtonText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 14,
    fontWeight: '500',
    color: '#ee8c2b',
  },
});
